// Resource.Ref — version-level reference to a Resource, whole-or-part.
//
// Per `.docs/v1/design/bounded-contexts.md` §1 (Slice 7a widening) and
// `open-questions.md` §19 Q9 landing. Refs always carry `(id, version)`;
// bare ids are never enough. Slice 7a widens the shape to a sum so
// annotations (and future consumers) can anchor at sub-resource
// granularity from day one.
//
// Discriminants `whole_resource_ref` / `part_resource_ref` are globally
// unique across BCs. Both variants share `id: ResourceId` and
// `version: Version` so pre-Slice-7a code that destructured `ref.id`
// or `ref.version` keeps working through the sum unchanged.
//
// **`ElementLocator` is intentionally opaque in MVP** — a bare
// `NonEmpty<string>` brand with no internal structure. A decidable
// narrow taxonomy (`PageRegionLocator`, `TextSpanLocator`,
// `HtmlElementLocator`, …) lands additively at Slice 7.5+ when the
// first renderer or evaluator adapter demands typed locators.
// Middle-ground: locking whole-only is a lifecycle mistake (users
// highlight PDF pages on day 1); locking the full taxonomy is scope
// explosion.
//
// **Migration.** Slice-2's `mkResourceRef(id, version)` is renamed
// `mkWholeResourceRef` — the Slice-2 default remains the whole-ref
// path; part-refs are constructed via the new `mkPartResourceRef`.
// Existing call sites that used the alias `mk` (via `Resource.Ref.mk`)
// migrate to `Resource.Ref.mkWhole`.
export { mkElementLocator, mkPart, mkPartResourceRef, mkWhole, mkWholeResourceRef, };
const mkElementLocator = (s) => s;
const mkWholeResourceRef = (id, version) => ({
    type: "whole_resource_ref",
    id,
    version,
});
const mkPartResourceRef = (id, version, locator) => ({
    type: "part_resource_ref",
    id,
    version,
    locator,
});
// === Short-name aliases ===
//
// The composer surfaces `Resource.Ref.mkWhole` / `Resource.Ref.mkPart`
// for the common-case call sites. Full-name aliases remain available
// for callers who prefer explicit `mkWholeResourceRef` /
// `mkPartResourceRef`.
const mkWhole = mkWholeResourceRef;
const mkPart = mkPartResourceRef;
//# sourceMappingURL=ref.js.map